import React, { useState, useEffect, useRef } from 'react';
import { BiSearch, BiX } from 'react-icons/bi';
import productServices from '../../services/ProductsServices';
import { useNavigate } from 'react-router-dom';

function Search() {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate()
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch suggestions (first page only)
    useEffect(() => {
        if (!query.trim()) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const delayDebounce = setTimeout(async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({ search: query });
                const res = await productServices.getProducts(`?${params.toString()}`);
                const results = res.data?.results || [];
                setSuggestions(results);
                setShowSuggestions(true);
            } catch (err) {
                console.error('Failed to fetch suggestions:', err);
                setSuggestions([]);
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [query]);

    const handleSelect = (product) => {
        setQuery(product.name);
        setShowSuggestions(false);
        navigate(`/product/${product.slug}`);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!query.trim()) return;
        navigate(`/products?search=${encodeURIComponent(query)}`);
    };

    return (
        <div className="w-full max-w-md" ref={wrapperRef}>
            <form onSubmit={handleSubmit} className="relative">
                <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2 border md:border-none w-full">
                    <BiSearch className="text-gray-500 dark:text-gray-400 h-5 w-5 flex-shrink-0" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onFocus={() => query && setShowSuggestions(true)}
                        placeholder="Search products..."
                        className="ml-2 w-full bg-transparent border-none outline-none focus:ring-0
                       text-gray-900 dark:text-white
                       placeholder:text-gray-500 dark:placeholder:text-gray-400
                       min-w-0 text-sm sm:text-base"
                    />
                    {query && (
                        <button
                            type="button"
                            onClick={() => {
                                setQuery('');
                                setSuggestions([]);
                                setShowSuggestions(false);
                            }}
                            className="ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            aria-label="Clear search"
                        >
                            <BiX className="h-5 w-5" />
                        </button>
                    )}
                </div>

                {/* Suggestions Dropdown */}
                {showSuggestions && (
                    <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-auto">
                        {loading ? (
                            <div className="px-4 py-2 text-gray-500 dark:text-gray-400 text-sm">Searching...</div>
                        ) : suggestions.length > 0 ? (
                            suggestions.map((product) => (
                                <div
                                    key={product.id || product.slug}
                                    onClick={() => handleSelect(product)}
                                    className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm flex items-center gap-3 min-h-[40px]"
                                >
                                    <div className="flex-shrink-0">
                                        <img
                                            className="h-10 w-10 rounded-lg object-cover"
                                            src={product.image}
                                            alt={product.name}
                                        />
                                    </div>
                                    <div className="font-medium text-gray-900 dark:text-white line-clamp-1 truncate">
                                        {product.name}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="px-4 py-2 text-gray-500 dark:text-gray-400 text-sm">
                                No products found
                            </div>
                        )}
                    </div>
                )}
            </form>
        </div>
    );
}

export default Search;